import DateFnsUtils from '@date-io/moment'; // choose your lib
import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { Button } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import Hidden from '@material-ui/core/Hidden';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import React, { useState } from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  RouteComponentProps
} from 'react-router-dom';
import '../00_init';
import { BackgroundJobsProvider } from '../hooks/useBackgroundJobs';
import { HotkeysProvider } from '../hooks/useHotkeys';
import { SnackbarProviderConfigured } from '../hooks/useSnackbar';
import { Centered } from '../layout/Centered';
import { COMBINED_THEME, MUI_THEME } from '../themes';
import { SafeExecuteButton } from './components/SafeExecuteButton';
import { Login } from './features/Login';
import Header from './layout/Header';
import Navigator from './layout/Navigator';
import { initialSetup } from './services/admin';
import {
  CurrentAdminUserProvider,
  signOut,
  useAdminLogin
} from './services/auth';
import { LOOSE_PAGES, PAGES } from './sitemap';

const drawerWidth = 256;
const drawerBackgroundColor = '#0a1017';

const RootContainer = styled('div')((p) => ({
  display: 'flex',
  minHeight: '100vh'
}));

const Drawer = styled('nav')((p) => ({
  [p.theme.breakpoints.up('sm')]: {
    width: drawerWidth,
    flexShrink: 0
  }
}));

const AppContent = styled('div')((p) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
}));

const MainContent = styled('main')((p) => ({
  flex: 1,
  padding: '48px 36px 0',
  background: '#eaeff1'
}));

const AppThemeProvider: React.FC = ({ children }) => (
  <MuiThemeProvider theme={MUI_THEME}>
    {(() => (
      <ThemeProvider theme={COMBINED_THEME}>{children}</ThemeProvider>
    ))()}
  </MuiThemeProvider>
);

const AppContext: React.FC<{ userId: string }> = ({ userId, children }) => {
  return (
    <CurrentAdminUserProvider userId={userId}>
      <HotkeysProvider>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <AppThemeProvider>
            <SnackbarProviderConfigured>
              <BackgroundJobsProvider>{children}</BackgroundJobsProvider>
            </SnackbarProviderConfigured>
          </AppThemeProvider>
        </MuiPickersUtilsProvider>
      </HotkeysProvider>
    </CurrentAdminUserProvider>
  );
};

// To go to setup mode you need to:
// - Set SETUP_MODE to true
// - Set SETUP_ENV to the environment you'd like
// - Allow access to the CF admin-grantAdminClaim
// - Allow create access to the adminUsers collection
const SETUP_MODE = false;
const SETUP_ENV = 'DEV';
const SetupMode = () => {
  return (
    <AppThemeProvider>
      <SnackbarProviderConfigured>
        <Centered height={500}>
          <div>
            <SafeExecuteButton
              variant="contained"
              color="secondary"
              onClick={async (execute) => {
                if (SETUP_ENV) {
                  return initialSetup(SETUP_ENV, execute);
                }
              }}
            >
              Initial Setup
            </SafeExecuteButton>
          </div>

          <div>
            <Button onClick={() => signOut()}>Sign out</Button>
          </div>
        </Centered>
      </SnackbarProviderConfigured>
    </AppThemeProvider>
  );
};

const App = () => {
  const { adminUser, loading, error, hasAuthUser } = useAdminLogin();
  const [drawerOpen, toggleDrawer] = useState(false);

  if (hasAuthUser && SETUP_MODE) {
    return <SetupMode />;
  }

  if (error) {
    console.log(error);
    signOut();
    return <div>Error</div>;
  }

  if (loading) {
    return null;
  }

  if (!adminUser) {
    return (
      <AppThemeProvider>
        <Login />
      </AppThemeProvider>
    );
  }

  return (
    <AppContext userId={adminUser.uid}>
      <Router>
        <RootContainer>
          <CssBaseline />
          <Drawer>
            <Hidden smUp implementation="js">
              <Navigator
                PaperProps={{
                  style: {
                    width: drawerWidth,
                    backgroundColor: drawerBackgroundColor
                  }
                }}
                variant="temporary"
                open={drawerOpen}
                onClose={() => toggleDrawer(!drawerOpen)}
              />
            </Hidden>
            <Hidden xsDown implementation="css">
              <Navigator
                PaperProps={{
                  style: {
                    width: drawerWidth,
                    backgroundColor: drawerBackgroundColor
                  }
                }}
              />
            </Hidden>
          </Drawer>

          <AppContent>
            <Route path="/" exact render={() => <Redirect to="/orgs" />} />
            {PAGES.map((page) => {
              const tabPaths = page.tabs.map((t) => t.path);
              return (
                <Route
                  key={page.path}
                  exact
                  path={`${page.path}/:tab(${tabPaths.join('|')})?`}
                  render={(props: RouteComponentProps) => {
                    const activeTab =
                      page.tabs.find(
                        (t) => (props.match.params as any)['tab'] === t.path
                      ) || page.tabs[0];
                    const tabIndex = page.tabs.indexOf(activeTab);
                    return (
                      <>
                        <Header
                          onDrawerToggle={() => toggleDrawer(!drawerOpen)}
                          adminUser={adminUser}
                          title={page.title}
                          tabs={page.tabs.map((t) => t.label)}
                          activeTabIndex={tabIndex}
                          onChange={(e: any, i: number) => {
                            const nextTab = page.tabs[i];
                            if (!nextTab) {
                              return;
                            }
                            props.history.push(`${page.path}/${nextTab.path}`);
                          }}
                        />
                        <MainContent>{activeTab.render(props)}</MainContent>
                      </>
                    );
                  }}
                />
              );
            })}
            {LOOSE_PAGES.map((p) => (
              <Route
                key={p.path}
                path={p.path}
                exact
                render={(props) => <MainContent>{p.render(props)}</MainContent>}
              />
            ))}
          </AppContent>
        </RootContainer>
      </Router>
    </AppContext>
  );
};

export default App;
